// API Client for RAG Backend

import type { TestParameter, User, UsersListResponse, Project, ProjectsListResponse, ProjectSortByField, SortOrder, Questionnaire, QuestionnairesListResponse, QuestionnaireAnswersListResponse, Question, QuestionsListResponse, FilesListResponse, FilesUploadResponse } from '@/shared/lib/types';
import { translate } from '@/shared/lib/locales';

const API_BASE = '/api/v1';

// Os códigos do backend têm o formato "namespace.chave", onde `namespace` é um dos
// objetos JÁ EXISTENTES nos ficheiros de locale (ex. "documentsSection.fileNotFoundError")
// — a mensagem vive ao lado das restantes traduções da área funcional a que pertence,
// em vez de um bloco genérico dedicado a mensagens do backend.
export function translateBackendCode(code: string, params?: Record<string, unknown>): string {
  const dotIndex = code.indexOf('.');
  if (dotIndex === -1) return code;
  const namespace = code.slice(0, dotIndex);
  const key = code.slice(dotIndex + 1);
  return translate(namespace, key, params || {});
}

// O backend devolve os erros como { detail: { code, params } } (código estável +
// parâmetros de interpolação). Traduzimos o código no idioma ativo via i18next.
// Retrocompatibilidade: se o detail vier como string (código ainda não migrado),
// mostramos o texto tal como veio.
export function resolveBackendMessage(detail: unknown, fallback: string): string {
  if (typeof detail === 'string') return detail || fallback;
  if (detail && typeof detail === 'object' && 'code' in detail) {
    const { code, params } = detail as { code?: string; params?: Record<string, unknown> };
    if (code) return translateBackendCode(code, params);
  }
  return fallback;
}

function safeJsonParse(text: string): any {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

class APIClient {
  private sessionId: string | null = null;
  private sessionToken: string | null = null;

  // Callback invocado quando o backend rejeita a sessão (401). Registado pela app
  // (ver sessionStore) para limpar a sessão e reenviar o utilizador para o login.
  // É um callback em vez de um import direto do store para evitar dependência circular.
  onUnauthorized: (() => void) | null = null;
  // Guard de reentrância: evita que vários 401 concorrentes disparem o callback
  // repetidamente. Reposto a false quando uma nova sessão é criada (setSessionToken).
  private handlingUnauthorized = false;

  setSessionId(sessionId: string | null) {
    this.sessionId = sessionId;
  }

  // JWT de sessão: enviado como Authorization: Bearer em cada pedido. É a prova de
  // identidade stateless que qualquer réplica do backend consegue validar.
  setSessionToken(token: string | null) {
    this.sessionToken = token;
    if (token) {
      // Nova sessão válida: reabilita o tratamento de futuros 401.
      this.handlingUnauthorized = false;
    }
  }

  // Reage a um 401 do backend: dispara o callback de sessão expirada uma única vez.
  private handleUnauthorized() {
    if (this.handlingUnauthorized) return;
    this.handlingUnauthorized = true;
    this.onUnauthorized?.();
  }

  getSessionToken(): string | null {
    return this.sessionToken;
  }

  // Anexa o JWT de sessão como query param. Necessário para URLs abertos diretamente
  // pelo browser (downloads, relatórios), onde não é possível enviar o header Authorization.
  private withToken(url: string): string {
    if (!this.sessionToken) return url;
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}token=${encodeURIComponent(this.sessionToken)}`;
  }

  // Anexa o timezone do browser como query param, para que datas/horas no relatório
  // sejam calculadas na hora local de quem o pediu, e não na do servidor.
  private withTimezone(url: string): string {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}tz=${encodeURIComponent(tz)}`;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    timeoutMs: number = 30000, // default 30 seconds
    parseJson: boolean = true
  ): Promise<T> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    };

    if (this.sessionId) {
      (headers as Record<string, string>)['X-Session-ID'] = this.sessionId;
    }

    if (this.sessionToken) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${this.sessionToken}`;
    }

    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        headers,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        if (response.status === 401) {
          this.handleUnauthorized();
        }
        const error = await response.json().catch(() => ({ detail: 'Request failed' }));
        throw new Error(resolveBackendMessage(error.detail, translate('common', 'requestFailedError')));
      }

      return (parseJson ? response.json() : undefined) as T;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error(translate('common', 'requestTimeoutError'));
      }
      throw error;
    }
  }

  // Sessions
  async createSession(email: string): Promise<{ session_id: string; token: string }> {
    return this.request('/sessions', { method: 'POST', body: JSON.stringify({ email }) });
  }

  async getSession(sessionId: string): Promise<any> {
    return this.request(`/sessions/${sessionId}`);
  }

  async deleteSession(sessionId: string): Promise<void> {
    return this.request(`/sessions/${sessionId}`, { method: 'DELETE' });
  }

  // Renova o JWT de sessão (mesma identidade, novo `exp`) sem exigir novo login. O
  // token actual (ainda válido) é a credencial — ver sessions.py:refresh_session.
  async refreshSession(sessionId: string): Promise<{ token: string }> {
    return this.request(`/sessions/${sessionId}/refresh`, { method: 'POST' });
  }

  // Config
  async getConfig(): Promise<any> {
    return this.request('/config');
  }

  async updateConfig(config: Partial<any>): Promise<any> {
    return this.request('/config', {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  }

  async getModels(): Promise<{ models: any[] }> {
    return this.request('/config/models');
  }

  async testConnection(): Promise<any> {
    return this.request('/config/test-connection', { method: 'POST' });
  }

  // Documents

  async startFolderIndexing(sourceFolder: string, destinationFolder?: string): Promise<any> {
    return this.request('/documents/index', {
      method: 'POST',
      body: JSON.stringify({
        source_folder: sourceFolder,
        destination_folder: destinationFolder,
      }),
    });
  }

  async startIndexing(projectId: string): Promise<any> {
    return this.request(`/projects/${projectId}/files/index`, {
      method: 'POST',
      body: JSON.stringify({
        session_id: this.sessionId,
      }),
    });
  }

  /** Reenfileira TODOS os ficheiros do projeto cujo processamento falhou em
   * definitivo — um único pedido, não um por ficheiro (ver
   * FilesController.retry_failed_indexing). */
  async retryFailedProjectFiles(projectId: string): Promise<{ message: string }> {
    return this.request(`/projects/${projectId}/files/retry-failed`, {
      method: 'POST',
    });
  }

  async cancelIndexing(projectId: string): Promise<void> {
    await this.request(`/projects/${projectId}/files/index/cancel`, {
      method: 'POST',
      body: JSON.stringify({ session_id: this.sessionId }),
    }, 30000, false);
  }

  async cancelQuestionnaireRun(projectId: string): Promise<void> {
    await this.request(`/projects/${projectId}/run/cancel`, {
      method: 'POST',
      body: JSON.stringify({ session_id: this.sessionId }),
    }, 30000, false);
  }

  /** Cancela a execução de UM questionário, sem afetar os outros
   * questionários do mesmo projeto que estejam a correr (ao contrário de
   * `cancelQuestionnaireRun`, à escala do projeto inteiro). */
  async cancelSingleQuestionnaireRun(questionnaireId: string): Promise<void> {
    await this.request(`/questionnaires/${questionnaireId}/run/cancel`, {
      method: 'POST',
      body: JSON.stringify({ session_id: this.sessionId }),
    }, 30000, false);
  }

  async getIndexingStatus(): Promise<any> {
    return this.request('/documents/status');
  }

  async getDocuments(): Promise<any> {
    return this.request('/documents');
  }

  async clearDocuments(): Promise<void> {
    return this.request('/documents', { method: 'DELETE' });
  }

  async scanFolder(folderPath: string): Promise<any> {
    return this.request('/documents/scan', {
      method: 'POST',
      body: JSON.stringify({ folder_path: folderPath }),
    });
  }

  // Questions
  async getQuestions(): Promise<{ questions: any[]; total: number }> {
    return this.request('/questions');
  }

  // Query
  async executeQuery(question: string, useCache = true): Promise<any> {
    return this.request('/query', {
      method: 'POST',
      body: JSON.stringify({ question, include_sources: true, use_cache: useCache }),
    }, 120000); // 2 minutes for single query
  }

  async executeBatchQuery(questions?: string[]): Promise<any> {
    return this.request('/query/batch', {
      method: 'POST',
      body: JSON.stringify({ questions }),
    }, 600000); // 10 minutes for batch query
  }

  async getQueryHistory(): Promise<any> {
    return this.request('/query/history');
  }

  async clearCache(): Promise<void> {
    return this.request('/query/cache', { method: 'DELETE' });
  }

  // Reports
  async generateReport(options?: { include_questions?: string[]; report_title?: string }): Promise<any> {
    // Report generation can take 5+ minutes (processes all questions)
    return this.request('/reports/generate', {
      method: 'POST',
      body: JSON.stringify(options || {}),
    }, 600000); // 10 minutes timeout
  }

  async getReportStatus(reportId: string): Promise<any> {
    return this.request(`/reports/${reportId}`);
  }

  getReportDownloadUrl(reportId: string): string {
    return this.withToken(`${API_BASE}/reports/${reportId}/download`);
  }

  async listReports(): Promise<any> {
    return this.request('/reports');
  }

  // Report Tests
  async getTestParameters(): Promise<Object> {
    return this.request('/tests/parameters');
  }

  async executeTest(
    selectedParameterName: string, 
    parameters: TestParameter[],
    selectedQuestions: string[]
  ): Promise<any> {
    console.log("Parâmetro de teste enviado para execução: ", selectedParameterName);
    console.log("Todos os parâmetros de teste: ", parameters);
    console.log("Questões selecionadas: ", selectedQuestions);

    const paramsRec = parameters.reduce((dict, param) => {
      if (dict.hasOwnProperty(param.section))
        dict[param.section][param.name] = { current_value: param.current_value, range: param.range };
      else
        dict[param.section] = { [param.name]: { current_value: param.current_value, range: param.range } };

      return dict;
    }, {} as Record<string, any>);

    const body = JSON.stringify({ 
        'selected_parameter': selectedParameterName, 
        'parameters': paramsRec,
        'selected_questions': selectedQuestions 
      });

    console.log("Corpo da requisição de teste: ", body);
    return this.request('/tests/execute', {
      headers: {
        'X-Session-ID': this.sessionId ?? '',
        ...(this.sessionToken ? { Authorization: `Bearer ${this.sessionToken}` } : {}),
      },
      method: 'POST',
      body: body,
    }, 6000000); // 10 minutes timeout
  }

  async getTestResultDownloadUrl(testId: number): Promise<any> {
    // Assuming the backend provides a direct download endpoint for test results
    return this.request(`/tests/${testId}/download`, {method: 'GET'}, 600000); // 10 minutes timeout
  }

  // Users
  async getUsers(params?: { userId?: string; email?: string }): Promise<UsersListResponse> {
    const query = new URLSearchParams();
    if (params?.userId) query.set('user_id', params.userId);
    if (params?.email) query.set('email', params.email);
    const qs = query.toString();
    return this.request(`/users${qs ? `?${qs}` : ''}`);
  }

  async createUser(name: string, email: string, roleId?: string): Promise<User> {
    return this.request('/users', {
      method: 'POST',
      body: JSON.stringify({ name, email, role_id: roleId ?? null }),
    });
  }

  async deleteUser(userId: string): Promise<void> {
    return this.request(`/users/${userId}`, { method: 'DELETE' });
  }

  // Projects
  async getProjects(
    sessionId: string,
    params?: { pageNumber?: number; pageSize?: number; sortBy?: ProjectSortByField | null; sortOrder?: SortOrder | null },
  ): Promise<ProjectsListResponse> {
    const query = new URLSearchParams();
    query.set('session_id', sessionId);
    if (params?.pageNumber) query.set('page_number', String(params.pageNumber));
    if (params?.pageSize) query.set('page_size', String(params.pageSize));
    if (params?.sortBy) query.set('sort_by', params.sortBy);
    if (params?.sortOrder) query.set('sort_order', params.sortOrder);
    return this.request(`/projects?${query.toString()}`);
  }

  async getProject(projectId: string): Promise<Project> {
    return this.request(`/projects/${projectId}`);
  }

  async createProject(name: string): Promise<string> {
    return this.request('/projects', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  async updateProject(projectId: string, data: { name: string }): Promise<Project> {
    return this.request(`/projects/${projectId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteProject(projectId: string): Promise<void> {
    await this.request(`/projects/${projectId}`, { method: 'DELETE' }, 30000, false);
  }

  async addUserToProject(projectId: string, userId: string, role: string): Promise<void> {
    await this.request(`/projects/${projectId}/user/${userId}`, {
      method: 'POST',
      body: JSON.stringify({ role }),
    }, 30000, false);
  }

  async updateUserInProject(projectId: string, userId: string, role: string): Promise<void> {
    await this.request(`/projects/${projectId}/user/${userId}`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    }, 30000, false);
  }

  async removeUserFromProject(projectId: string, userId: string): Promise<void> {
    await this.request(`/projects/${projectId}/user/${userId}`, { method: 'DELETE' }, 30000, false);
  }

  // Questionnaires
  async getQuestionnaires(projectId?: string, onlyOriginal?: boolean): Promise<QuestionnairesListResponse> {
    const params = new URLSearchParams();
    if (projectId) params.set('project_id', projectId);
    if (onlyOriginal) params.set('only_original', 'true');
    const query = params.toString();
    return this.request(`/questionnaires${query ? `?${query}` : ''}`);
  }

  async getQuestionnaire(questionnaireId: string): Promise<Questionnaire> {
    return this.request(`/questionnaires/${questionnaireId}`);
  }

  async getQuestionnaireAnswers(questionnaireId: string): Promise<QuestionnaireAnswersListResponse> {
    return this.request(`/questionnaires/${questionnaireId}/answers`);
  }

  async createQuestionnaire(projectId: string, name: string, createdBy?: string): Promise<Questionnaire> {
    return this.request(`/projects/${projectId}/questionnaires`, {
      method: 'POST',
      body: JSON.stringify({ name, created_by: createdBy ?? null }),
    });
  }

  async deleteQuestionnaire(questionnaireId: string): Promise<void> {
    await this.request(`/questionnaires/${questionnaireId}`, { method: 'DELETE' }, 30000, false);
  }

  getQuestionnaireReportUrl(questionnaireId: string): string {
    return this.withTimezone(this.withToken(`${API_BASE}/questionnaires/${questionnaireId}/report`));
  }

  async runQuestionnaire(questionnaireId: string): Promise<Questionnaire> {
    return this.request(`/questionnaires/${questionnaireId}/run`, {
      method: 'POST',
      body: JSON.stringify({ session_id: this.sessionId }),
    });
  }

  /** Pede o cálculo de confiança das respostas que ficaram sem ele (fase de
   * confiança cancelada ou falhada) — ver
   * `QuestionnairesController.calculate_missing_confidence`. */
  async calculateQuestionnaireConfidence(questionnaireId: string): Promise<{ message: string }> {
    return this.request(`/questionnaires/${questionnaireId}/confidence`, {
      method: 'POST',
    });
  }

  async retryFailedQuestionnaireAnswers(questionnaireId: string): Promise<{ message: string }> {
    return this.request(`/questionnaires/${questionnaireId}/retry-failed`, {
      method: 'POST',
    });
  }

  async runProjectQuestionnaires(projectId: string): Promise<boolean> {
    return this.request(`/projects/${projectId}/run`, {
      method: 'POST',
      body: JSON.stringify({ session_id: this.sessionId }),
    });
  }

  getProjectReportUrl(projectId: string): string {
    return this.withTimezone(this.withToken(`${API_BASE}/projects/${projectId}/report`));
  }

  async updateQuestionnaire(questionnaireId: string, data: { name?: string; project_id?: string | null }): Promise<Questionnaire> {
    return this.request(`/questionnaires/${questionnaireId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async duplicateQuestionnaire(questionnaireId: string, projectId: string): Promise<string> {
    return this.request(`/questionnaires/${questionnaireId}/duplicate`, {
      method: 'POST',
      body: JSON.stringify({ project_id: projectId }),
    });
  }

  async createNewQuestionnaire(name: string, questions: string[], projectId: string): Promise<Questionnaire> {
    return this.request('/questionnaires', {
      method: 'POST',
      body: JSON.stringify({ name, questions, project_id: projectId }),
    });
  }

  // Questionnaire Questions
  async getQuestionnaireQuestions(projectId: string, questionnaireId: string): Promise<QuestionsListResponse> {
    return this.request(`/projects/${projectId}/questionnaires/${questionnaireId}/questions`);
  }

  async createQuestionnaireQuestion(questionnaireId: string, text: string, order: number): Promise<Question> {
    return this.request(`/questionnaires/${questionnaireId}/questions`, {
      method: 'POST',
      body: JSON.stringify({ question: text, order }),
    });
  }

  async createQuestionnaireQuestionsBatch(questionnaireId: string, texts: string[]): Promise<void> {
    await this.request(`/questionnaires/${questionnaireId}/questions/batch`, {
      method: 'POST',
      body: JSON.stringify({ questions: texts }),
    }, 30000, false);
  }

  async updateQuestionnaireQuestion(questionnaireId: string, questionId: string, text: string, order: number): Promise<void> {
    await this.request(`/questionnaires/${questionnaireId}/questions/${questionId}`, {
      method: 'PUT',
      body: JSON.stringify({ question: text, order: order }),
    }, 30000, false);
  }

  async deleteQuestionnaireQuestion(projectId: string, questionnaireId: string, questionId: string): Promise<void> {
    await this.request(`/questionnaires/${questionnaireId}/questions/${questionId}`, { method: 'DELETE' }, 30000, false);
  }

  async reorderQuestions(questionnaireId: string, questionIds: string[]): Promise<void> {
    await this.request(`/questionnaires/${questionnaireId}/questions/reorder`, {
      method: 'PATCH',
      body: JSON.stringify({ question_ids: questionIds }),
    }, 30000, false);
  }

  /** Pede o cálculo de confiança da resposta a UMA pergunta — ver
   * `QuestionnairesController.calculate_question_confidence`. */
  async calculateQuestionConfidence(questionnaireId: string, questionId: string): Promise<{ message: string }> {
    return this.request(`/questionnaires/${questionnaireId}/questions/${questionId}/confidence`, {
      method: 'POST',
    });
  }

  // Project Files
  async getProjectFiles(projectId: string): Promise<FilesListResponse> {
    return this.request(`/projects/${projectId}/files`);
  }

  // Usa XMLHttpRequest (não fetch) para poder reportar progresso real de upload
  // via `upload.onprogress` — fetch não expõe isso. onProgress recebe 0-100
  // (progresso agregado do pedido, não por-ficheiro: todos os ficheiros do
  // lote vão num único pedido, para a indexação poder arrancar de forma
  // determinística assim que este pedido terminar, sem debounce).
  uploadProjectFiles(
    projectId: string,
    files: File[],
    uploadedBy?: string,
    onProgress?: (percent: number) => void,
  ): Promise<FilesUploadResponse> {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    if (uploadedBy) formData.append('uploaded_by', uploadedBy);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_BASE}/projects/${projectId}/files`);
      xhr.timeout = 120000; // 2 minutes for upload
      if (this.sessionId) xhr.setRequestHeader('X-Session-ID', this.sessionId);
      if (this.sessionToken) xhr.setRequestHeader('Authorization', `Bearer ${this.sessionToken}`);

      xhr.upload.onprogress = (event) => {
        if (onProgress && event.lengthComputable) {
          onProgress((event.loaded / event.total) * 100);
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch {
            reject(new Error(translate('common', 'requestFailedError')));
          }
          return;
        }
        if (xhr.status === 401) {
          this.handleUnauthorized();
        }
        const error = safeJsonParse(xhr.responseText) ?? { detail: 'Upload failed' };
        reject(new Error(resolveBackendMessage(error.detail, translate('common', 'requestFailedError'))));
      };

      xhr.onerror = () => reject(new Error(translate('common', 'requestFailedError')));
      xhr.ontimeout = () => reject(new Error(translate('common', 'requestTimeoutError')));

      xhr.send(formData);
    });
  }

  getProjectFileDownloadUrl(projectId: string, fileId: string): string {
    return this.withToken(`${API_BASE}/projects/${projectId}/files/${fileId}/download`);
  }

  async deleteProjectFile(projectId: string, fileId: string): Promise<void> {
    await this.request(`/projects/${projectId}/files/${fileId}`, { method: 'DELETE' }, 30000, false);
  }
}

export const api = new APIClient();
